import { cloneDeep } from "lodash";

import State from "./State";
import { BaseShipment } from "./BaseShipment";
import { TMapAttribute } from "../types/TMapAttribute";
import { Alarm } from "./Alarm";
import { TDeviceConfig } from "./TDeviceConfig";
import PairHistory from "./PairHistory";
import { DeviceConfig } from "../services/DeviceConfigService";
import { THistoryTableConfig, TLocationProperty, TDeviceAttribute } from "../types";
import { IAnyObject } from "../interfaces";
import { parseState } from "./StateParser";

export class Device {
	public id: string;
	public displayName: string;
	public type: string;
	public prefix: string;
	public present: State;
	/**
	  * @deprecated The variable should not be used
	  */
	public shipment: BaseShipment;
	public config: TDeviceConfig;
	public alarms: Alarm[];
	public lastPairedDevice: string;
	public lastPairedDeviceDate: number;
	public hasSendumTracker: boolean;
	public pairHistory: PairHistory[];
	public releasedDate: number;
	public returnedDate: number;

	constructor(data: IAnyObject) {
		const deviceData: IAnyObject = data.data as IAnyObject || {};
		const present = data.present as IAnyObject;

		if (!deviceData.deviceId) {
			throw new Error("[Device] device ID doesn't exist");
		}

		this.id = deviceData.deviceId as string;
		this.type = deviceData.type ? (deviceData.type as string).toUpperCase() : "ENVIROTAINER";
		this.config = this.getConfig();
		this.prefix = deviceData.prefix as string;
		this.displayName = this.prefix && this.id.startsWith(this.prefix + "-") ? this.id.substring(this.prefix.length + 1) : this.id;
		this.present = parseState(present, this.type);
		this.shipment = new BaseShipment(data.shipment as IAnyObject);
		this.lastPairedDevice = deviceData.lastPairedDevice as string;
		this.lastPairedDeviceDate = !!present && present["lastPairedDevice:clock"] ? parseInt(present["lastPairedDevice:clock"] as string, 10) : 0;
		this.alarms = this.parseAlarms(present, data.clearedAlarms as IAnyObject[]);
		this.hasSendumTracker = deviceData.hasSendumTracker as boolean;
		this.pairHistory = data.pairHistory ? (data.pairHistory as IAnyObject[]).map((item: IAnyObject) => new PairHistory(item)) : [];
		this.returnedDate = deviceData.returnedDate as number;
		this.releasedDate = deviceData.releasedDate as number;
	}

	private getConfig(): TDeviceConfig {
		switch (this.type) {
			case "ENVIROTAINER": {
				return DeviceConfig.getEnvirotainerAlarmsConfig();
			}
			case "PT300":
			case "PT300D": {
				return DeviceConfig.getPT300AlarmsConfig();
			}
			default:
				return DeviceConfig.getEnvirotainerAlarmsConfig();
		}
	}

	private parseAlarms(present: IAnyObject, clearedAlarms?: IAnyObject[]): Alarm[] {
		const alarms = new Array<Alarm>();

		if (this.config) {
			if (present) {
				Object.keys(present).forEach(key => {
					for (const alarmConfig of this.config.alarms) {
						const matchedKey = alarmConfig.pattern.exec(key);
						if (matchedKey !== null) {
							alarms.push(new Alarm({
								sensor: key,
								displayName: `${alarmConfig.displayName}${matchedKey[2] ? ` ID ${matchedKey[2]}` : ""}`,
								timestamp: parseInt(present[`${key}`] as string, 10),
							}));
							break;
						}
					}
				});
			}

			clearedAlarms?.forEach(a => {
				for (const alarmConfig of this.config.alarms) {
					const matchedKey = alarmConfig.pattern.exec(a.Sensor as string);
					if (matchedKey !== null) {
						alarms.push(new Alarm({
							sensor: a.Sensor,
							timestamp: parseInt(a.Original as string, 10),
							reason: a.Reason,
							clearedBy: a.WidgetUserId || a.User,
							clearedTimestamp: parseInt(a.Cleared as string, 10),
							displayName: `${alarmConfig.displayName}${matchedKey[2] ? ` ID ${matchedKey[2]}` : ""}`,
						}));
						break;
					}
				}
			});
		}

		return alarms;
	}

	public isContainer(): boolean {
		return this.hasSendumTracker || !!this.lastPairedDevice;
	}

	public updateAlarms(present: IAnyObject): void {
		if (this.config) {
			Object.keys(present).forEach(key => {
				for (const alarmConfig of this.config.alarms) {
					const matchedKey = alarmConfig.pattern.exec(key);
					if (matchedKey !== null) {
						const index = this.alarms.findIndex(a => !a.clearedBy && a.sensor === key);
						const alarm = new Alarm({
							sensor: key,
							displayName: `${alarmConfig.displayName}${matchedKey[2] ? ` ID ${matchedKey[2]}` : ""}`,
							timestamp: this.getAlarmTime(parseInt(present[`${key}`] as string, 10)),
						});
						if (index === -1) {
							this.alarms.push(alarm);
						} else {
							this.alarms[index] = alarm;
						}
						break;
					}
				}
			});
		}
	}

	public getAlarmTime(time: number): number {
		if (this.lastPairedDeviceDate) {
			const startDate = this.lastPairedDeviceDate > this.releasedDate ? this.lastPairedDeviceDate : this.releasedDate;

			return time > startDate ? time : startDate;
		}

		return time;

	}

	public getDisplayName(): string {
		if (this.lastPairedDevice) {
			return `${this.displayName} (${this.lastPairedDevice})`;
		}

		return this.displayName;
	}

	public getAttributesConfig(): Map<string, TDeviceAttribute> {
		switch (this.type) {
			case "ENVIROTAINER": {
				return /^(ENV-)(RKN|RAP)\w+$/.test(this.id) ? DeviceConfig.getETechAttributesConfig() : DeviceConfig.getEnvirotainerAttributesConfig();
			}
			case "PT300D":
			case "PT300": {
				return DeviceConfig.getPT300AttributesConfig();
			}
			default:
				return DeviceConfig.getEnvirotainerAttributesConfig();
		}
	}

	public getSelectedSensorsConfig(maxSensors = 6): string[] {
		let sensors: string[] = [];
		switch (this.type) {
			case "ENVIROTAINER": {
				sensors = /^(ENV-)(RKN|RAP)\w+$/.test(this.id) ? ["insideTemperatureMax"] : ["insideTemperatureMax", "insideTemperatureMin"];
				break;
			}
			case "PT300D":
			case "PT300": {
				sensors = ["tempProbe", "temperature_c", "orientation", "humidity", "battery", "pressure"]
					.filter(key => Array.from(DeviceConfig.getPT300AttributesConfig().keys()).includes(key));
				break;
			}
			default: {
				sensors = ["insideTemperatureMax", "insideTemperatureMin"];
				break;
			}
		}

		return sensors.slice(0, maxSensors);
	}

	public getSensorsMultiSelectConfig(): boolean {
		switch (this.type) {
			case "ENVIROTAINER": {
				return true;
			}
			case "PT300D":
			case "PT300": {
				return true;
			}
			default:
				return true;
		}
	}

	public getMapAttributesConfig(): TMapAttribute {
		switch (this.type) {
			case "ENVIROTAINER": {
				return /^(ENV-)(RKN|RAP)\w+$/.test(this.id) ? DeviceConfig.getETechMapConfig() : DeviceConfig.getEnvirotainerMapConfig();
			}
			case "PT300D":
			case "PT300": {
				return DeviceConfig.getPT300MapConfig();
			}
			default:
				return DeviceConfig.getEnvirotainerMapConfig();
		}
	}

	public getHistoryTableConfig(): THistoryTableConfig {
		switch (this.type) {
			case "ENVIROTAINER": {
				return DeviceConfig.getEnvirotainerHistoryTableConfig();
			}
			case "PT300D":
			case "PT300": {
				return DeviceConfig.getPT300HistoryTableConfig();
			}
			default:
				return DeviceConfig.getEnvirotainerHistoryTableConfig();
		}
	}

	public getAlarms(sensors?: string[]): Alarm[] {
		if (!sensors) {
			return this.alarms;
		}

		return this.alarms.filter(alarm => sensors.includes(alarm.sensor));
	}

	public getActiveAlarms(): Alarm[] {
		return this.alarms.filter(alarm => !alarm.clearedBy);
	}

	public getClearedAlarms(): Alarm[] {
		return this.alarms.filter(alarm => !!alarm.clearedBy);
	}
	// used for get location if the location not exists in "present". Need for black pins.
	public getLocation(): TLocationProperty {
		if (this.present.location) {
			return this.present.location;
		}

		if (this.shipment.segments) {
			const firstNode = this.shipment.segments.filter(_ => _.segmentType === "Node").shift();
			if (firstNode && !!firstNode.nodeLat && !!firstNode.nodeLng) {
				return {
					latitude: Number.parseFloat(firstNode.nodeLat),
					longitude: Number.parseFloat(firstNode.nodeLng),
				};
			}
		}

		return undefined;
	}

	public clone(): Device {
		return cloneDeep(this);
	}
}
